<template>
  <div class="permission-matrix">
    <div v-if="title" class="matrix-header">
      <h5>{{ title }}</h5>
      <button v-if="permissions.length" type="button" class="btn btn-outline-secondary btn-sm" @click="toggleAll">
        {{ allSelected ? 'Clear all' : 'Select all' }}
      </button>
    </div>

    <div v-if="!permissions.length" class="matrix-empty">
      No permissions available.
    </div>

    <div v-for="group in permissions" :key="group.module" class="matrix-group">
      <div class="matrix-group-head">
        <strong>{{ group.label }}</strong>
        <label class="matrix-check">
          <input
            type="checkbox"
            :checked="isGroupSelected(group)"
            @change="toggleGroup(group, $event.target.checked)"
          />
          <span>Select all</span>
        </label>
      </div>

      <div class="matrix-grid">
        <label v-for="permission in group.permissions" :key="permission.permissionid" class="matrix-check matrix-item">
          <input
            type="checkbox"
            :checked="selectedSet.has(String(permission.permissionid))"
            @change="togglePermission(permission.permissionid, $event.target.checked)"
          />
          <span>
            <strong>{{ permission.permission_key }}</strong>
            <small>{{ permission.description || 'No description' }}</small>
          </span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PermissionMatrix',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    permissions: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    selectedSet() {
      return new Set((this.modelValue || []).map(String));
    },
    allPermissionIds() {
      return this.permissions.flatMap((group) => group.permissions.map((permission) => String(permission.permissionid)));
    },
    allSelected() {
      return this.allPermissionIds.length > 0 && this.allPermissionIds.every((id) => this.selectedSet.has(id));
    },
  },
  methods: {
    emitSelection(values) {
      this.$emit('update:modelValue', [...new Set(values.map(String))]);
    },
    togglePermission(permissionId, checked) {
      const next = new Set(this.selectedSet);
      const normalized = String(permissionId);
      if (checked) {
        next.add(normalized);
      } else {
        next.delete(normalized);
      }
      this.emitSelection(Array.from(next));
    },
    toggleGroup(group, checked) {
      const next = new Set(this.selectedSet);
      for (const permission of group.permissions) {
        const normalized = String(permission.permissionid);
        if (checked) {
          next.add(normalized);
        } else {
          next.delete(normalized);
        }
      }
      this.emitSelection(Array.from(next));
    },
    toggleAll() {
      this.emitSelection(this.allSelected ? [] : this.allPermissionIds);
    },
    isGroupSelected(group) {
      return group.permissions.length > 0 && group.permissions.every((permission) => this.selectedSet.has(String(permission.permissionid)));
    },
  },
};
</script>

<style scoped>
.permission-matrix { display:flex; flex-direction:column; gap:16px; }
.matrix-header { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.matrix-header h5 { margin:0; font-size:1rem; }
.matrix-empty { padding:16px; border:1px dashed #d1d5db; border-radius:10px; color:#6b7280; font-size:.9rem; }
.matrix-group { border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; }
.matrix-group-head { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 14px; background:#f9fafb; border-bottom:1px solid #e5e7eb; }
.matrix-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(260px, 1fr)); gap:10px; padding:14px; }
.matrix-check { display:flex; align-items:flex-start; gap:10px; margin:0; }
.matrix-check input { margin-top:4px; }
.matrix-item { padding:10px 12px; border:1px solid #e5e7eb; border-radius:10px; background:#fff; }
.matrix-item span { display:flex; flex-direction:column; gap:4px; }
.matrix-item strong { font-size:.88rem; color:#111827; }
.matrix-item small { color:#6b7280; line-height:1.35; }
</style>
